/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MATTER_KIND_LABEL } from '@kd/shared';
import { PartyPicker } from '@/components/PartyPicker';
import { Badge, Button, Empty, ErrorText, Field, Input, Modal, PageHeader, Pager, Select, Spinner, Textarea } from '@/components/ui';
import { api, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtDate } from '@/lib/format';
import { useDebounced, useDirectory } from '@/lib/hooks';

export function MattersPage() {
  const { can } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('OPEN');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const dq = useDebounced(q);
  const { data, isLoading } = useQuery({ queryKey: ['matters', dq, kind, status, page], queryFn: () => api.get(`/matters${qs({ q: dq, kind, status, page, pageSize: 50 })}`) });
  return (
    <>
      <PageHeader title="Дела" actions={can('matter.write') && <Button onClick={() => setCreating(true)}>Новое дело</Button>} />
      <div className="card">
        <div className="flex flex-wrap gap-2 border-b border-slate-200 p-3">
          <Input className="max-w-xs" placeholder="Название, клиент, номер" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
          <Select className="max-w-[220px]" value={kind} onChange={(e) => setKind(e.target.value)} placeholder="Все виды" options={Object.entries(MATTER_KIND_LABEL).map(([value, label]) => ({ value, label }))} />
          <Select className="max-w-[160px]" value={status} onChange={(e) => setStatus(e.target.value)} placeholder="Все" options={[{ value: 'OPEN', label: 'В работе' }, { value: 'CLOSED', label: 'Закрытые' }]} />
        </div>
        {isLoading ? <Spinner /> : !data?.items.length ? <Empty>Дел нет</Empty> : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead className="bg-slate-50"><tr><th className="th">№</th><th className="th">Дело</th><th className="th">Вид</th><th className="th">Клиент</th><th className="th">Ответственный</th><th className="th">Открыто</th></tr></thead>
            <tbody>{data.items.map((m: any) => (
              <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="td tabular-nums">{m.number}</td>
                <td className="td"><Link className="inline-flex items-center gap-1 font-medium text-brand-700 hover:underline" to={`/matters/${m.id}`}>{m.confidential && <Lock size={12} className="text-amber-600" />}{m.title}</Link></td>
                <td className="td"><Badge>{MATTER_KIND_LABEL[m.kind]}</Badge></td><td className="td">{m.clientName ?? '-'}</td><td className="td">{m.ownerName}</td><td className="td">{fmtDate(m.openedAt)}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
        {data && <Pager page={page} pageSize={50} total={data.total} onPage={setPage} />}
      </div>
      <Modal title="Новое дело" open={creating} onClose={() => setCreating(false)}>
        <MatterForm onSaved={(m) => nav(`/matters/${m.id}`)} />
      </Modal>
    </>
  );
}

export function MatterForm({ matter, onSaved }: { matter?: any; onSaved: (m: any) => void }) {
  const qc = useQueryClient();
  const { data: people } = useDirectory();
  const [f, setF] = useState({
    title: matter?.title ?? '', kind: matter?.kind ?? 'COURT', clientId: matter?.clientId ?? null, clientName: matter?.clientName ?? '',
    ownerId: matter?.ownerId ?? '', confidential: matter?.confidential ?? false, description: matter?.description ?? '',
  });
  const save = useMutation({
    mutationFn: () => {
      const { clientName, ...rest } = f;
      const body = { ...rest, ownerId: f.ownerId || undefined, description: f.description || null };
      return matter ? api.patch(`/matters/${matter.id}`, body) : api.post('/matters', body);
    },
    onSuccess: (m) => { qc.invalidateQueries({ queryKey: ['matters'] }); qc.invalidateQueries({ queryKey: ['matter'] }); onSaved(m); },
  });
  const submit = (e: FormEvent) => { e.preventDefault(); save.mutate(); };
  return (
    <form onSubmit={submit} className="space-y-3">
      <Field label="Название"><Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Например: Банкротство ООО «Антея», А14-12862/2026" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Вид"><Select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} options={Object.entries(MATTER_KIND_LABEL).map(([value, label]) => ({ value, label }))} /></Field>
        <Field label="Ответственный"><Select value={f.ownerId} onChange={(e) => setF({ ...f, ownerId: e.target.value })} placeholder="Я" options={(people ?? []).map((p) => ({ value: p.id, label: p.fullName }))} /></Field>
      </div>
      <Field label="Клиент"><PartyPicker value={f.clientId} valueLabel={f.clientName} onChange={(id, n) => setF({ ...f, clientId: id, clientName: n ?? '' })} /></Field>
      <Field label="Описание"><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.confidential} onChange={(e) => setF({ ...f, confidential: e.target.checked })} /> Конфиденциальное дело (видит только команда)</label>
      <ErrorText error={save.error} />
      <div className="flex justify-end"><Button type="submit" disabled={save.isPending}>Сохранить</Button></div>
    </form>
  );
}
